import { isLeft, isRight } from 'fp-ts/Either';
import { createContext, ReactNode, use, useEffect } from 'react';
import { createRequiredContextHook } from '../hooks/createRequiredContextHook';
import { useAsyncData } from '../hooks/useAsyncData';
import { AuthStateType, useAuth } from '../hooks/useAuth';
import { useTRPC } from '../hooks/useTRPC';
import { captureAndReportError } from '../utils/errorReporting';

interface DappData {
  id: string;
  dappName: string;
  subtitle?: string | null;
  description: string;
  dappIconUrl?: string | null;
  bannerUrl?: string | null;
  dappPreviewUrls?: string[];
  language?: string;
  appWebsite?: string | null;
  contactEmail: string;
  supportEmail: string;
  tokenDistribution: boolean;
  walletAddress?: string | null;
  licenseUrl?: string | null;
  copyrightUrl?: string | null;
  privacyPolicyUrl?: string | null;
}

interface DappDataContextValue {
  dappData: DappData | null;
  isLoading: boolean;
  error: string | null;
  refetchDapp: () => void;
}

const DappDataContext = createContext<DappDataContextValue | null>(null);

export function DappDataProvider({
  children,
  dappId,
}: {
  children: ReactNode;
  dappId: string;
}) {
  const trpc = useTRPC();
  const [authState] = useAuth();
  const {
    data: dappData,
    setData: setDappData,
    isLoading,
    setIsLoading,
    error,
    setError,
    invalidate,
    run,
  } = useAsyncData<DappData | null>({
    initialData: null,
    initialLoading: true,
  });

  const fetchDapp = () => {
    if (!dappId) {
      setError('No dapp ID provided');
      setIsLoading(false);
      return Promise.resolve();
    }

    return run(() => trpc.getDapp.query({ dappId }), {
      showLoadingState: false,
      onSuccess: (result, helpers) => {
        if ('_tag' in result && isLeft(result)) {
          helpers.setError(result.left.message);
          helpers.setIsLoading(false);
          return;
        }

        if ('_tag' in result && isRight(result)) {
          helpers.setData(result.right);
        }

        helpers.setError(null);
        helpers.setIsLoading(false);
      },
      onError: (err, helpers) => {
        captureAndReportError(err, {
          operation: 'Failed to load dapp data',
          component: 'DappDataProvider',
        });
        helpers.setError('Failed to load dapp data');
        helpers.setIsLoading(false);
      },
    }).then(() => undefined);
  };

  useEffect(() => {
    invalidate();
    setDappData(null);
    setError(null);
    setIsLoading(true);

    if (authState.type === AuthStateType.Authenticated) {
      const timer = setTimeout(() => {
        void fetchDapp();
      }, 100);

      return () => clearTimeout(timer);
    } else if (authState.type === AuthStateType.NoToken) {
      setError('Authentication required');
      setIsLoading(false);
    }
  }, [authState.type, dappId, invalidate, setDappData, setError, setIsLoading]);

  return (
    <DappDataContext.Provider
      value={{
        dappData,
        isLoading,
        error,
        refetchDapp: fetchDapp,
      }}
    >
      {children}
    </DappDataContext.Provider>
  );
}

export const useDappData = createRequiredContextHook(
  DappDataContext,
  'DappDataProvider',
  'useDappData',
);

export function useDappDataOptional() {
  const context = use(DappDataContext);
  return (
    context || {
      dappData: null,
      isLoading: false,
      error: null,
      refetchDapp: () => {},
    }
  );
}
